import {
  createContext,
  useContext,
  useEffect,
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { Link } from 'react-router-dom';
import { Icon, type IconName } from '../icons';

export { Select, type SelectChangeEvent, type SelectOption, type SelectProps } from './select';
export { FilterPill, SegmentedControl, Switch, type SegmentOption } from './controls';
export { Popover } from './popover';
export { StatusBadge, STATUSES, type Status, type StatusBadgeProps } from './StatusBadge';
export { ChannelGlyph, type Channel, type ChannelGlyphProps, type ChannelGlyphSize } from './ChannelGlyph';
export { KpiCard, type KpiCardProps } from './KpiCard';
export { Card, CARD_FRAME, LIST_ROW, type CardProps } from './Card';
export { Badge, type BadgeTone } from './Badge';
export { DataTable, isRowClick, type DataColumn } from './DataTable';
export { StatusTabs, statusPanelProps, type StatusTab } from './StatusTabs';

// --- Buttons ----------------------------------------------------------------------------

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * One button system (redesign DS): a flat primary, a bordered secondary, a
 * ghost for low emphasis, a filled danger and a quiet danger-ghost for a
 * destructive action that sits apart ("Supprimer le contact"). Hover only
 * applies while enabled; every disabled variant looks the same.
 */
const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-primary-600 text-white shadow-ds-xs enabled:hover:bg-primary-700',
  secondary: 'bg-surface text-ink shadow-ds-xs ring-1 ring-inset ring-border-strong enabled:hover:bg-surface-2',
  ghost: 'text-ink-2 enabled:hover:bg-surface-3 enabled:hover:text-ink',
  danger: 'bg-danger-600 text-white shadow-ds-xs enabled:hover:bg-danger-700',
  'danger-ghost': 'text-danger-700 enabled:hover:bg-danger-50',
};

/** sm 30px · md 36px · lg 44px, the touch size phone layouts use. */
const BUTTON_SIZES: Record<ButtonSize, string> = {
  sm: 'h-[30px] rounded-ds-sm px-2.5 text-ds-sm',
  md: 'h-9 rounded-ds-md px-3.5 text-ds-base',
  lg: 'h-11 rounded-ds-md px-[18px] text-[15px]',
};

const BUTTON_BASE =
  'touch-target inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap font-semibold transition-[background-color,box-shadow,color,transform] enabled:active:scale-[0.98] focus-visible:shadow-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-disabled-bg disabled:text-disabled-text disabled:shadow-none disabled:ring-0';

/** The button look for a `<Link>` or `<a>` that acts as a button. */
export function buttonClasses(variant: ButtonVariant = 'primary', size: ButtonSize = 'md'): string {
  return `${BUTTON_BASE} ${BUTTON_SIZES[size]} ${BUTTON_STYLES[variant]}`;
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: IconName;
  /**
   * Why the button is disabled ("Ajoutez une photo pour programmer cette
   * publication."): the redesign's ButtonWithReason. A non-empty reason
   * disables the button unless `disabled` says otherwise; while disabled, the
   * reason shows under it and is wired to it with `aria-describedby`.
   */
  disabledReason?: string | null;
  /** An action in flight: disabled, busy for assistive tech, with a spinner before the label. */
  loading?: boolean;
}

export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  disabledReason,
  loading = false,
  className = '',
  children,
  ...props
}: ButtonProps) {
  const reasonId = useId();
  const disabled = loading || (props.disabled ?? Boolean(disabledReason));
  const showReason = disabled && !loading && Boolean(disabledReason);
  const iconSize = size === 'sm' ? 'size-[14px]' : 'size-[15px]';
  const button = (
    <button
      {...props}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-describedby={showReason ? reasonId : props['aria-describedby']}
      className={`${buttonClasses(variant, size)} ${className}`}
    >
      {loading ? (
        <span aria-hidden className={`${iconSize} animate-spin rounded-full border-2 border-current border-t-transparent`} />
      ) : icon ? (
        <Icon name={icon} className={iconSize} />
      ) : null}
      {children}
    </button>
  );
  if (disabledReason === undefined || disabledReason === null) return button;
  return (
    <span className="inline-flex flex-col items-start gap-1.5">
      {button}
      {showReason ? <DisabledReason id={reasonId}>{disabledReason}</DisabledReason> : null}
    </span>
  );
}

/**
 * The "disabled with a reason" hint: one warning-coloured line under a
 * disabled action. Use it directly when the hint sits apart from the button,
 * and point the button's `aria-describedby` at its `id`.
 */
export function DisabledReason({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <span id={id} className="flex items-start gap-1.5 text-ds-xs font-medium text-warning-700">
      <Icon name="alert" className="mt-px size-[14px] shrink-0" />
      <span>{children}</span>
    </span>
  );
}

export interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  /** Accessible name — required, because the button has no text. */
  label: string;
  icon: IconName;
  variant?: 'secondary' | 'ghost';
  size?: ButtonSize;
}

const ICON_BUTTON_SIZES: Record<ButtonSize, string> = { sm: 'size-[30px] rounded-ds-sm', md: 'size-9 rounded-ds-md', lg: 'size-11 rounded-ds-md' };

/** A square icon-only button; the label becomes its `aria-label` and tooltip. */
export function IconButton({ label, icon, variant = 'secondary', size = 'md', className = '', ...props }: IconButtonProps) {
  return (
    <button
      type="button"
      title={label}
      {...props}
      aria-label={label}
      className={`touch-target inline-flex shrink-0 items-center justify-center transition-[background-color,color,transform] enabled:active:scale-[0.98] focus-visible:shadow-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:text-disabled-text ${
        ICON_BUTTON_SIZES[size]
      } ${
        variant === 'secondary'
          ? 'bg-surface text-ink shadow-ds-xs ring-1 ring-inset ring-border-strong enabled:hover:bg-surface-2'
          : 'text-ink-3 enabled:hover:bg-surface-3 enabled:hover:text-ink'
      } ${className}`}
    >
      <Icon name={icon} className={size === 'sm' ? 'size-[15px]' : 'size-4'} />
    </button>
  );
}

/** "‹ Publications" above a page title: the breadcrumb back to where the page is opened from. */
export function BackLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="touch-target -ms-1 inline-flex h-6 max-w-full items-center gap-1 self-start whitespace-nowrap rounded-ds-sm pe-1.5 text-ds-sm font-medium text-ink-3 transition-colors hover:text-ink"
    >
      <Icon name="chevronLeft" className="size-[14px] shrink-0" />
      <span className="truncate">{children}</span>
    </Link>
  );
}

// --- Form fields ------------------------------------------------------------------------

/**
 * A labelled form field (redesign DS). The label is always visible, never a
 * placeholder's job; an optional field says "· facultatif", a required one says
 * nothing. Placeholder copy starts with "ex. " and has its own colour (the base
 * `::placeholder` rule), so it never passes for a typed value.
 */
export function Field({
  label,
  hint,
  error,
  optional = false,
  note,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  optional?: boolean;
  /** A short qualifier after the label in place of "facultatif" ("visibles par l’équipe"). */
  note?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <label className="flex min-w-0 flex-col gap-1.5">
      <span className="text-ds-sm font-semibold text-ink">
        {label}
        {note || optional ? <span className="font-normal text-ink-3"> · {note ?? t('common.optional')}</span> : null}
      </span>
      {children}
      {error ? (
        <span className="flex items-start gap-1.5 text-ds-xs text-danger-700">
          <Icon name="alert" className="mt-px size-[14px] shrink-0" />
          <span>{error}</span>
        </span>
      ) : hint ? (
        <span className="text-ds-xs text-ink-3">{hint}</span>
      ) : null}
    </label>
  );
}

const CONTROL_BASE =
  'w-full rounded-ds-md border-0 bg-surface px-3 py-2.5 text-ds-base text-ink shadow-ds-xs ring-1 ring-border-strong transition-shadow hover:ring-ink-300 focus:outline-none focus:ring-2 focus:ring-primary-600 disabled:bg-disabled-bg disabled:text-disabled-text';

export function Input({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${CONTROL_BASE} ${className}`} />;
}

/** In-panel search input: filled style, transparent border. */
export function SearchInput({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full rounded-ds-md border-0 bg-surface-2 px-3 py-2.5 text-ds-base text-ink ring-1 ring-transparent transition-shadow focus:bg-surface focus:outline-none focus:ring-2 focus:ring-primary-600 ${className}`}
    />
  );
}

export function Textarea({ className = '', ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${CONTROL_BASE} min-h-24 ${className}`} />;
}

// --- Surfaces ---------------------------------------------------------------------------

export function Panel({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={`rounded-(--radius-panel) bg-(--color-surface) p-6 shadow-card ring-1 ring-(--color-line) ${className}`}
    >
      {children}
    </div>
  );
}

export type AlertTone = 'info' | 'warn' | 'err' | 'ok' | 'neutral';

/** Tone names used before the redesign, still accepted. */
type LegacyAlertTone = 'danger' | 'positive';

const ALERT_TONES: Record<AlertTone, string> = {
  info: 'bg-info-50 border-info-border text-info-700',
  warn: 'bg-warning-50 border-warning-border text-warning-700',
  err: 'bg-danger-50 border-danger-border text-danger-700',
  ok: 'bg-success-50 border-success-border text-success-700',
  neutral: 'bg-surface-2 border-border text-ink-2',
};

const LEGACY_TONES: Record<LegacyAlertTone, AlertTone> = { danger: 'err', positive: 'ok' };

/**
 * A banner or inline notice (redesign DS): info · warn · err · ok · neutral,
 * with an optional action on the right and an optional dismiss button. Also
 * the frame of read-only notices and the inbox's handover bar.
 */
export function Alert({
  tone = 'err',
  role = 'status',
  icon,
  action,
  onDismiss,
  dense = false,
  className = '',
  children,
}: {
  tone?: AlertTone | LegacyAlertTone;
  /** One line, centred on its action: a phone's handover bar. */
  dense?: boolean;
  /** `alert` interrupts a screen reader at once — for a refused submit, not for ambient notices. */
  role?: 'status' | 'alert';
  icon?: IconName;
  action?: ReactNode;
  onDismiss?: () => void;
  className?: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const resolved = tone in LEGACY_TONES ? LEGACY_TONES[tone as LegacyAlertTone] : (tone as AlertTone);
  return (
    <div
      role={role}
      className={`flex gap-2.5 rounded-ds-md border text-ds-sm ${dense ? 'items-center px-3 py-1.5' : 'items-start px-3.5 py-3'} ${ALERT_TONES[resolved]} ${className}`}
    >
      {icon ? <Icon name={icon} className={`shrink-0 ${dense ? 'size-4' : 'mt-px size-[18px]'}`} /> : null}
      <div className="min-w-0 flex-1">{children}</div>
      {action ? <div className="shrink-0">{action}</div> : null}
      {onDismiss ? (
        <IconButton label={t('common.dismiss')} icon="x" variant="ghost" size="sm" onClick={onDismiss} className="-my-1 shrink-0" />
      ) : null}
    </div>
  );
}

// --- Chips and tags ---------------------------------------------------------------------

export type ChipTone = 'neutral' | 'accent' | 'positive' | 'caution' | 'danger';

const CHIP_TONES: Record<ChipTone, string> = {
  neutral: 'bg-(--color-surface-sunken) text-(--color-ink-700)',
  accent: 'bg-(--color-accent-wash) text-(--color-accent-strong)',
  positive: 'bg-(--color-positive-wash) text-(--color-positive-text)',
  caution: 'bg-(--color-caution-wash) text-(--color-caution-text)',
  danger: 'bg-(--color-danger-wash) text-(--color-danger-text)',
};

/** A status or type label: "Draft", "Active", "+12%". One line, never wraps. */
export function Chip({
  children,
  tone = 'neutral',
  icon,
  className = '',
}: {
  children: ReactNode;
  tone?: ChipTone;
  icon?: IconName;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex h-[22px] shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 text-[11.5px] font-semibold ${CHIP_TONES[tone]} ${className}`}
    >
      {icon ? <Icon name={icon} className="size-3" /> : null}
      {children}
    </span>
  );
}

/**
 * Tag colors as full literal class strings, so Tailwind's scanner sees them.
 * Must stay in step with the server's TAG_COLORS list. The six the tag
 * editor offers (slate, violet, blue, green, amber, pink) use the refonte's
 * values; the other three remain for tags created before it. Each text
 * colour reads at 4.5:1 on its tint (test/contrast.test.ts): slate, amber and
 * green take the DS ink-3, warning-700 and success-700 (axe, Phase 12).
 */
const TAG_CHIP_STYLES: Record<string, string> = {
  slate: 'bg-[#ECEAF1] text-ink-3',
  red: 'bg-[#FFEDEA] text-[#C2402F]',
  orange: 'bg-orange-50 text-orange-700',
  amber: 'bg-[#FDF1DE] text-warning-700',
  green: 'bg-[#E5F4EC] text-success-700',
  teal: 'bg-teal-50 text-teal-700',
  blue: 'bg-[#E8EFFD] text-[#2B66D9]',
  violet: 'bg-[#EFEAFE] text-[#5B3FF0]',
  pink: 'bg-[#FBEEF6] text-[#B8327E]',
};

export function tagChipClasses(color: string): string {
  return TAG_CHIP_STYLES[color] ?? TAG_CHIP_STYLES.slate!;
}

export function TagChip({
  name,
  color,
  dot = false,
  onRemove,
}: {
  name: string;
  color: string;
  /** A dot in the tag's colour before the name (Contacts, R10). */
  dot?: boolean;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex h-[22px] max-w-full shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 text-[11.5px] font-semibold ${tagChipClasses(color)}`}
    >
      {dot ? <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-current" /> : null}
      <span className="truncate">{name}</span>
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('common.removeTag', { name })}
          className="touch-target -mr-1 flex size-4 items-center justify-center rounded opacity-60 transition-opacity hover:opacity-100"
        >
          <Icon name="x" className="size-3" />
        </button>
      ) : null}
    </span>
  );
}

// --- Empty state ------------------------------------------------------------------------

/**
 * Honest empty state: one icon tile, a title, one sentence, and at most one
 * call to action — never a copy of the page header's. `inline` sits inside a
 * card that already has a frame (a dashboard panel); the default is a solid
 * card (a dashed one read as a drop zone, A10-05). `tone="success"` is for an
 * empty list that is good news ("Aucun échec de publication").
 */
export function EmptyState({
  icon,
  title,
  body,
  action,
  secondaryAction,
  footer,
  inline = false,
  tone = 'default',
}: {
  icon?: IconName;
  title: string;
  body: string;
  action?: ReactNode;
  /** Only where the design pairs two entry points (connect a channel / set up replies). */
  secondaryAction?: ReactNode;
  /** A quiet link under the actions ("Download sample CSV"). */
  footer?: ReactNode;
  inline?: boolean;
  tone?: 'default' | 'success';
}) {
  const tile = tone === 'success' ? 'bg-success-50 text-success-700' : inline ? 'bg-surface-2 text-ink-3' : 'bg-primary-50 text-primary-700';
  return (
    <div
      className={`flex flex-col items-center text-center ${
        inline ? 'gap-2.5 px-5 pb-8 pt-7' : 'gap-2 rounded-ds-lg border border-border bg-surface px-6 py-10 shadow-ds-xs'
      }`}
    >
      <span aria-hidden className={`mb-1 flex size-11 items-center justify-center rounded-ds-lg ${tile}`}>
        <Icon name={icon ?? (tone === 'success' ? 'checkCircle' : 'sparkle')} className="size-5" />
      </span>
      <p className={inline ? 'text-ds-base font-semibold text-ink' : 'text-ds-lg text-ink'}>{title}</p>
      <p className={`max-w-[420px] text-ink-2 ${inline ? 'text-ds-sm' : 'text-ds-base'}`}>{body}</p>
      {action || secondaryAction ? (
        <div className="mt-1 flex flex-wrap justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      ) : null}
      {footer ? <div className="mt-1 text-[12.5px] font-semibold">{footer}</div> : null}
    </div>
  );
}

// --- Overlays ---------------------------------------------------------------------------

/**
 * Lightweight modal: backdrop click and Escape both close. The backdrop is a
 * fixed flex container that centers the card; the card itself never exceeds
 * the viewport — its body scrolls instead of the page.
 *
 * It renders through a portal on `document.body`, for the same reason the row
 * menu does (D-078): `position: fixed` resolves against the nearest ancestor
 * carrying a transform, filter or containment, not against the viewport. The
 * page wrapper runs `animate-page-enter` with `fill-mode: both`, so its final
 * `translateY(0)` stays applied forever — an identity transform, but enough to
 * become the containing block. Left in the tree, `inset-0` sized the backdrop
 * to the content column and centred the card 119px above the top of the
 * screen. A portal makes the modal immune to that class of bug rather than
 * dependent on no ancestor ever animating.
 */
export function Modal({
  title,
  onClose,
  children,
  wide = false,
  size,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
  /** `xl`: the New automation modal, 880px (R43). */
  size?: 'xl';
}) {
  // Escape is bound on the document, not the backdrop: nothing inside is
  // focused when the modal opens, so a local handler never fires.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(21,15,43,0.5)] p-6 backdrop-blur-[3px]"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`flex w-full ${size === 'xl' ? 'max-w-[880px] max-h-[min(90vh,788px)]' : `${wide ? 'max-w-2xl' : 'max-w-[500px]'} max-h-[min(82vh,640px)]`} animate-pop-in flex-col overflow-hidden rounded-(--radius-modal) bg-(--color-surface) shadow-modal`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between px-6 pb-4 pt-6">
          <h2 className="font-display text-lg tracking-[-0.01em]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={i18n.t('common.closeDialog')}
            className="touch-target flex size-[30px] items-center justify-center rounded-full bg-(--color-surface-sunken) text-(--color-ink-500) transition-colors hover:bg-(--color-line-faint) hover:text-(--color-ink-900)"
          >
            <Icon name="x" className="size-4" />
          </button>
        </div>
        <div className="overflow-y-auto overflow-x-hidden px-6 pb-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2.5 text-sm text-(--color-ink-500)" role="status">
      <span className="size-4 animate-spin rounded-full border-2 border-(--color-line) border-t-(--color-accent)" />
      {label}
    </div>
  );
}

// --- Page furniture -----------------------------------------------------------------------

/**
 * Set to 2 by a layout that already owns the page's h1 (Settings and its
 * sub-nav): the pages inside it then head their section with an h2.
 */
export const PageHeadingLevel = createContext<1 | 2>(1);

/**
 * One page header for every screen (redesign, A00-12): breadcrumb, then the
 * title with its status badge, then the subtitle; actions on the right. The
 * eyebrow stays for the dashboard's date line.
 */
export function PageHeader({
  eyebrow,
  breadcrumb,
  title,
  status,
  lede,
  actions,
}: {
  eyebrow?: string;
  /** Where the page is opened from ("‹ Publications"). */
  breadcrumb?: { to: string; label: string };
  title: string;
  /** A StatusBadge next to the title. */
  status?: ReactNode;
  lede?: string;
  actions?: ReactNode;
}) {
  const level = useContext(PageHeadingLevel);
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div className="flex min-w-0 flex-col gap-1">
        {breadcrumb ? <BackLink to={breadcrumb.to}>{breadcrumb.label}</BackLink> : null}
        {/* Inside a layout the eyebrow would repeat its h1 ("Settings" above "Settings"). */}
        {eyebrow && level === 1 ? (
          <p className="text-ds-xs font-semibold uppercase tracking-[0.04em] text-ink-3">{eyebrow}</p>
        ) : null}
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          {level === 2 ? (
            <h2 className="text-ds-xl tracking-[-0.01em]">{title}</h2>
          ) : (
            <h1 className="font-display text-(length:--text-display) leading-(--text-display--line-height) tracking-(--text-display--letter-spacing)">
              {title}
            </h1>
          )}
          {status}
        </div>
        {lede ? <p className="max-w-xl text-ds-base text-ink-2">{lede}</p> : null}
      </div>
      {/* max-w-full: a shrink-0 row keeps its one-line width and would never wrap on a phone. */}
      {actions ? <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Shimmering placeholder block for content that is on its way. */
export function Skeleton({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={`block animate-pulse rounded-md bg-(--color-ink-300)/25 ${className}`}
    />
  );
}

/** Loading stand-in for a list/table panel: keeps the layout from jumping. */
export function ListSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div
      role="status"
      aria-label={i18n.t('common.loadingList')}
      className="overflow-hidden rounded-(--radius-panel) bg-(--color-surface) shadow-card ring-1 ring-(--color-line)"
    >
      <div className="border-b border-(--color-line-faint) px-5 py-3">
        <Skeleton className="h-3 w-40" />
      </div>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-6 border-b border-(--color-line-faint) px-5 py-4 last:border-0">
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/2 opacity-60" />
          </div>
          <Skeleton className="h-5 w-16" />
          <Skeleton className="hidden h-3 w-20 sm:block" />
        </div>
      ))}
    </div>
  );
}

type StatTone = 'neutral' | 'positive' | 'caution';

const STAT_DETAIL_TONES: Record<StatTone, string> = {
  neutral: 'text-(--color-ink-500)',
  positive: 'text-(--color-positive-text)',
  caution: 'text-(--color-caution-text)',
};

/** Dashboard metric tile. Real numbers only — never placeholder data. */
export function StatTile({
  label,
  value,
  detail,
  detailTone = 'neutral',
  to,
}: {
  label: string;
  value: string | number;
  detail?: string;
  detailTone?: StatTone;
  to?: string;
}) {
  const body = (
    <>
      <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-(--color-ink-500)">{label}</p>
      <p className="tabular mt-3 font-display text-[30px] leading-none text-(--color-ink-900)">{value}</p>
      {detail ? <p className={`mt-2 text-xs font-medium ${STAT_DETAIL_TONES[detailTone]}`}>{detail}</p> : null}
    </>
  );
  const frame = 'block rounded-(--radius-panel) bg-(--color-surface) p-5 shadow-card ring-1 ring-(--color-line)';
  if (to) {
    return (
      <Link
        to={to}
        className={`${frame} transition-all hover:-translate-y-0.5 hover:shadow-[0_10px_28px_-14px_rgba(27,23,48,0.25)]`}
      >
        {body}
      </Link>
    );
  }
  return <div className={frame}>{body}</div>;
}
